import React, {
  HTMLAttributes,
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react";
import { useRouter } from "next/router";
import cx from "classnames";
import { Hooks } from "@xt/xtbase";
const { useTranslation } = Hooks;
import { useStore } from "../store";
import { get_faCheckSendCode, get_faGetFaResultV2 } from "../api/uaapi/user";

import { Button } from "antd";
import Dynamic from "../components/Dynamic";
import _AntdModalAlert from "../components/AntdModalAlert";
import _FormItemInput from "../components/FormItem/Input";
import { FaCheckItemProps } from "../Modal";
import { VerifyTypeEnum } from "../ItemButton";

const AntdModalAlert = _AntdModalAlert as any;
const FormItemInput = _FormItemInput as any;

import styles from "./index.module.scss";
import stylesCommon from "../common.module.scss";
import { openLivehelp100Support } from "@/utils/livehelp100";

interface Props extends HTMLAttributes<HTMLDivElement> {
  type: VerifyTypeEnum;
  faBizType: number;
  dynamicCodeType: string;
  faCheckItemData: FaCheckItemProps;
  isFaResult?: boolean;
  successCallback?: (arg: any) => void;
  errorCallback?: (arg: any) => void;
  setDisabled?: (arg: boolean) => void;
  onSwitch?: () => void;
  isHideSecurity?: boolean;
  dynamicDisableClick?: boolean;
  geetestCbRef?: any;
  geetestRef?: any;
  msgSendDataObj?: any;
}

const Main: React.FC<Props> = ({
  type,
  faBizType,
  dynamicCodeType,
  faCheckItemData,
  isFaResult,
  successCallback,
  errorCallback,
  setDisabled,
  onSwitch,
  isHideSecurity,
  dynamicDisableClick,
  geetestCbRef,
  geetestRef,
  msgSendDataObj,
  className,
}) => {
  const t = useTranslation();
  const { isSubAcc } = useStore();

  const router = useRouter();

  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setDisabled && setDisabled(loading);
    return () => {
      setDisabled && setDisabled(false);
    };
  }, [loading]);

  const [isErr, setIsErr] = useState(false);

  const [code, setCode] = useState("");
  useEffect(() => setIsErr(false), [code]);

  const sendCodeEmailRef = useRef<any>();
  const [sendCodeEmailClickNumber, setSendCodeEmailClickNumber] = useState(0);
  const handleNoReceiveEmailCode = useCallback(() => {
    AntdModalAlert({
      width: 400,
      icon: false,
      title: t("@xt/Passkey.noReceiveCodeTipTitle"),
      content: (
        <div>
          <div style={{ marginBottom: 16 }}>
            {t("@xt/Passkey.noReceiveCodeTipDesc")}
          </div>
          <div style={{ marginBottom: 16 }}>
            {t("@xt/Passkey.noReceiveCodeTipStep1")}
          </div>
          <div style={{ marginBottom: 16 }}>
            {t("@xt/Passkey.noReceiveCodeTipStep2", [faCheckItemData.email])}
          </div>
          <div>{t("@xt/Passkey.noReceiveCodeTipStep3")}</div>
        </div>
      ),
    });
  }, [faCheckItemData]);
  const handleSendCodeEmailClick = useCallback(() => {
    geetestCbRef.current = sendCodeEmailRef.current;
    geetestRef.current &&
      geetestRef.current.verify &&
      geetestRef.current.verify();
  }, []);

  const sendCodeMobileRef = useRef<any>();
  const sendCodeMobileVoiceRef = useRef<any>();
  const [sendCodeMobileDisabled, setSendCodeMobileDisabled] = useState(false);
  const handleSendCodeMobileClick = useCallback(() => {
    geetestCbRef.current = sendCodeMobileRef.current;
    geetestRef.current &&
      geetestRef.current.verify &&
      geetestRef.current.verify();
  }, []);
  const handleSendCodeMobileVoiceClick = useCallback(() => {
    geetestCbRef.current = sendCodeMobileVoiceRef.current;
    geetestRef.current &&
      geetestRef.current.verify &&
      geetestRef.current.verify();
  }, []);

  const [isCanPaste, setIsCanPaste] = useState(false);
  useEffect(() => {
    if (navigator.clipboard) setIsCanPaste(true);
  }, []);
  const handlePaste = useCallback(async () => {
    const text = await navigator.clipboard.readText();
    const val = text.slice(0, 6);
    if (/^[0-9]+$/.test(val)) {
      setCode(val);
    }
  }, []);

  const handleSecurity = useCallback(() => {
    // 原：跳转 /accounts/user/reset-verification（拼接 faCheckId、backUrl）
    // let url = (router.locale ? `/${router.locale}` : "") + "/accounts/user/reset-verification"; ...
    // location.href = url;
    openLivehelp100Support();
  }, []);
  const handleSubmit = useCallback(() => {
    if (!code || code.length < 6) return setIsErr(true);
    setIsErr(false);
    setLoading(true);

    const { faCheckId } = faCheckItemData;

    get_faCheckSendCode({
      params: {
        faCheckId,
        codeType: dynamicCodeType,
        code,
        validateItemType: (() => {
          const obj = {
            [VerifyTypeEnum.email]: 0,
            [VerifyTypeEnum.mobile]: 1,
            [VerifyTypeEnum.google]: 2,
          };
          return obj[type];
        })(),
      },
      errorPop: true,
    })
      .then(async (data) => {
        if (!isFaResult) {
          try {
            successCallback && await successCallback(data);
          } catch (e) {
            //empty
          }
          setLoading(false);
          return;
        }
        get_faGetFaResultV2({
          params: {
            faCheckId,
            faBizType,
          },
          errorPop: true,
        })
          .then(async (data) => {
            successCallback && await successCallback(data);
          })
          .catch(async (e) => {
            errorCallback && await errorCallback(e);
          })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch(async (e) => {
        try {
          errorCallback && await errorCallback(e);
        } catch (e) {
          //empty
        }
        setLoading(false);
      });
  }, [
    type,
    faBizType,
    faCheckItemData,
    dynamicCodeType,
    isFaResult,
    successCallback,
    code,
  ]);

  const formItemInputProps = {
    className: styles.formItemInput,
    type: "tel",
    inputMode: "numeric",
    maxLength: 6,
    isErr: isErr,
    disabled: loading,
    value: code,
    onInput: (val) => /^[0-9]*$/.test(val) && setCode(val),
  };

  return (
    <div className={cx(styles.main, className)}>
      {type === VerifyTypeEnum.email && (
        <div className={styles.formItem}>
          <p>
            {t("@xt/Passkey.emailCodeTip", [faCheckItemData.email])}
            {isSubAcc && <span>{`(${t("@xt/Passkey.mainAccount")})`}</span>}
          </p>
          <FormItemInput
            {...formItemInputProps}
            suffix={
              <Dynamic
                ref={sendCodeEmailRef}
                disabled={loading}
                codeType={dynamicCodeType}
                faCheckId={faCheckItemData.faCheckId}
                msgSendDataObj={msgSendDataObj}
                msgType={2}
                noEmailChange={() =>
                  setSendCodeEmailClickNumber(sendCodeEmailClickNumber + 1)
                }
                disableClick={dynamicDisableClick}
                onClick={handleSendCodeEmailClick}
              />
            }
          />
        </div>
      )}
      {type === VerifyTypeEnum.mobile && (
        <div className={styles.formItem}>
          <p>
            {t("@xt/Passkey.mobileCodeTip", [faCheckItemData.mobile])}
            {isSubAcc && <span>{`(${t("@xt/Passkey.mainAccount")})`}</span>}
          </p>
          <FormItemInput
            {...formItemInputProps}
            suffix={
              <Dynamic
                ref={sendCodeMobileRef}
                disabled={loading || sendCodeMobileDisabled}
                codeType={dynamicCodeType}
                faCheckId={faCheckItemData.faCheckId}
                countryCode={faCheckItemData.countryCode}
                msgType={0}
                updateDisabled={setSendCodeMobileDisabled}
                disableClick={dynamicDisableClick}
                onClick={handleSendCodeMobileClick}
              />
            }
          />
        </div>
      )}
      {type === VerifyTypeEnum.google && (
        <div className={styles.formItem}>
          <p>
            {t("@xt/Passkey.googleCodeTip")}
            {isSubAcc && <span>{`(${t("@xt/Passkey.mainAccount")})`}</span>}
          </p>
          <FormItemInput
            {...formItemInputProps}
            suffix={
              isCanPaste && (
                <button className={cx(stylesCommon.btnTxt)} onClick={handlePaste}>
                  {t("@xt/Passkey.copy")}
                </button>
              )
            }
          />
        </div>
      )}

      <div className={styles.option}>
        <div>
          {!isHideSecurity && !isSubAcc && (
            <button className={cx(stylesCommon.btnTxt)} onClick={handleSecurity}>
              {t("@xt/Passkey.security")}
            </button>
          )}
        </div>
        {type === VerifyTypeEnum.email && sendCodeEmailClickNumber > 1 && (
          <button className={cx(stylesCommon.btnTxt)} onClick={handleNoReceiveEmailCode}>
            {t("@xt/Passkey.noReceiveCode")}
          </button>
        )}
        {/* 语音验证码功能已注释 */}
        {/* {type === VerifyTypeEnum.mobile && (
          <Dynamic ref={sendCodeMobileVoiceRef} className={styles.voiceCode} label={t("@xt/Passkey.voiceCode")} msgType={1} ... />
        )} */}
      </div>

      <Button
        type="primary"
        size="large"
        block
        className={cx(styles.antdBtn)}
        onClick={handleSubmit}
        loading={loading}
      >
        {t("@xt/Passkey.confirm")}
      </Button>
      {!!onSwitch && (
        <div className={cx(styles.switchDiv)}>
          <button
            className={cx(stylesCommon.btnTxt)}
            disabled={loading}
            onClick={onSwitch}
          >
            {t("@xt/Passkey.switchVerifyMethod")}
          </button>
        </div>
      )}
    </div>
  );
};

export default Main;
